import { useState, useEffect, useCallback, useRef } from "react";
import { Trans, t } from "@lingui/macro";
import cx from "classnames";

import Modal from "../Modal/Modal";
import { useNotifyModalState } from "lib/useNotifyModalState";
import useWallet from "lib/wallets/useWallet";
import {
  loadPrefs,
  savePrefs,
  makePriceAlertId,
  requestBrowserPermission,
  browserNotificationState,
  type NotificationPrefs,
  type PriceAlert,
} from "lib/notifications/notificationPrefs";

import "./NotifyModal.scss";

const NOTIF_API = `${import.meta.env.VITE_INDEXER_API || ""}/api/notifications`;

const AVAILABLE_SYMBOLS = [
  // Crypto markets
  "BTC", "ETH", "BNB", "SOL", "XRP", "DOGE", "ADA", "TRX", "LINK", "AVAX",
  "SHIB", "TON", "DOT", "NEAR", "MATIC", "LTC", "UNI", "ATOM", "ARB", "OP", "BESC",
  // Tokenized stocks & commodities
  "AAPL", "TSLA", "NVDA", "MSFT", "GOOGL", "AMZN", "META", "SPY", "QQQ", "GOLD",
];

export function NotifyModal() {
  const { notifyModalOpen, setNotifyModalOpen } = useNotifyModalState();
  const { account } = useWallet();
  const [prefs, setPrefs] = useState<NotificationPrefs>(loadPrefs);
  const [permState, setPermState] = useState(browserNotificationState);
  const [newAlertSymbol, setNewAlertSymbol] = useState("BTC");
  const [newAlertPrice, setNewAlertPrice] = useState("");
  const [newAlertDir, setNewAlertDir] = useState<"above" | "below">("above");
  const [addingAlert, setAddingAlert] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const emailTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Re-read prefs when modal opens; load email from backend if wallet connected
  useEffect(() => {
    if (!notifyModalOpen) return;
    setPrefs(loadPrefs());
    setPermState(browserNotificationState());
    setEmailSaved(false);
    if (account) {
      fetch(`${NOTIF_API}/${account}`)
        .then((r) => r.json())
        .then((d) => { if (d?.email) setEmail(d.email); })
        .catch(() => {});
    }
  }, [notifyModalOpen, account]);

  const saveEmail = useCallback((val: string) => {
    if (!account) return;
    setEmailSaving(true);
    fetch(`${NOTIF_API}/${account}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: val }),
    })
      .then(() => { setEmailSaved(true); setEmailSaving(false); })
      .catch(() => setEmailSaving(false));
  }, [account]);

  const handleEmailChange = useCallback((val: string) => {
    setEmail(val);
    setEmailSaved(false);
    clearTimeout(emailTimeout.current);
    if (val.includes("@")) {
      emailTimeout.current = setTimeout(() => saveEmail(val), 900);
    }
  }, [saveEmail]);

  const persist = useCallback((next: NotificationPrefs) => {
    setPrefs(next);
    savePrefs(next);
  }, []);

  const handleEnableBrowser = useCallback(async () => {
    if (permState === "denied") return;
    const granted = await requestBrowserPermission();
    setPermState(browserNotificationState());
    if (granted) {
      persist({ ...prefs, browserEnabled: true });
    }
  }, [permState, prefs, persist]);

  const togglePref = useCallback(
    (key: keyof Pick<NotificationPrefs, "browserEnabled" | "liquidationRisk" | "tradeConfirmations">) => {
      persist({ ...prefs, [key]: !prefs[key] });
    },
    [prefs, persist]
  );

  const addPriceAlert = useCallback(() => {
    const price = parseFloat(newAlertPrice);
    if (!price || price <= 0) return;
    const alert: PriceAlert = {
      id: makePriceAlertId(),
      symbol: newAlertSymbol,
      targetPrice: price,
      direction: newAlertDir,
      enabled: true,
    };
    persist({ ...prefs, priceAlerts: [...prefs.priceAlerts, alert] });
    setNewAlertPrice("");
    setAddingAlert(false);
  }, [newAlertPrice, newAlertSymbol, newAlertDir, prefs, persist]);

  const toggleAlert = useCallback(
    (id: string) => {
      persist({
        ...prefs,
        priceAlerts: prefs.priceAlerts.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a)),
      });
    },
    [prefs, persist]
  );

  const deleteAlert = useCallback(
    (id: string) => {
      persist({ ...prefs, priceAlerts: prefs.priceAlerts.filter((a) => a.id !== id) });
    },
    [prefs, persist]
  );

  const isGranted = permState === "granted";
  const isDenied = permState === "denied";
  const isUnsupported = permState === "unsupported";

  return (
    <Modal isVisible={notifyModalOpen} setIsVisible={setNotifyModalOpen} label={t`Notifications`}>
      <div className="NM-root">

        {/* ── Browser push permission ─────────────────────── */}
        <section className="NM-section">
          <div className="NM-section-header">
            <span className="NM-section-title"><Trans>Browser Notifications</Trans></span>
            {isGranted && (
              <Toggle
                checked={prefs.browserEnabled}
                onChange={() => togglePref("browserEnabled")}
                label={prefs.browserEnabled ? t`On` : t`Off`}
              />
            )}
          </div>

          {isUnsupported && (
            <div className="NM-warn">
              <Trans>Your browser does not support notifications.</Trans>
            </div>
          )}

          {isDenied && (
            <div className="NM-warn">
              <Trans>
                Notifications are blocked in your browser. To enable, open your browser's site settings and
                allow notifications for this site, then reload the page.
              </Trans>
            </div>
          )}

          {!isGranted && !isDenied && !isUnsupported && (
            <div className="NM-permission-row">
              <p className="NM-permission-body">
                <Trans>
                  Enable browser push notifications to get real-time alerts about your positions,
                  liquidation risk, and price targets.
                </Trans>
              </p>
              <button className="NM-btn-primary" onClick={handleEnableBrowser}>
                🔔 <Trans>Enable Notifications</Trans>
              </button>
            </div>
          )}
        </section>

        {/* ── Alert types ─────────────────────────────────── */}
        {isGranted && prefs.browserEnabled && (
          <>
            <div className="NM-divider" />
            <section className="NM-section">
              <div className="NM-section-title NM-mb8"><Trans>Alert Types</Trans></div>

              <AlertRow
                icon="⚠️"
                title={t`Liquidation Risk`}
                body={t`Alert when a position is approaching liquidation (< 15% margin health)`}
                checked={prefs.liquidationRisk}
                onChange={() => togglePref("liquidationRisk")}
              />

              <AlertRow
                icon="✅"
                title={t`Trade Confirmations`}
                body={t`Notify when an order or position change is confirmed on-chain`}
                checked={prefs.tradeConfirmations}
                onChange={() => togglePref("tradeConfirmations")}
              />
            </section>

            {/* ── Price alerts ──────────────────────────────── */}
            <div className="NM-divider" />
            <section className="NM-section">
              <div className="NM-section-header NM-mb8">
                <span className="NM-section-title"><Trans>Price Alerts</Trans></span>
                {!addingAlert && (
                  <button className="NM-btn-add" onClick={() => setAddingAlert(true)}>
                    + <Trans>Add</Trans>
                  </button>
                )}
              </div>

              {addingAlert && (
                <div className="NM-add-alert-form">
                  <div className="NM-add-alert-row">
                    <select
                      className="NM-select"
                      value={newAlertSymbol}
                      onChange={(e) => setNewAlertSymbol(e.target.value)}
                    >
                      {AVAILABLE_SYMBOLS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>

                    <select
                      className="NM-select"
                      value={newAlertDir}
                      onChange={(e) => setNewAlertDir(e.target.value as "above" | "below")}
                    >
                      <option value="above">{t`Above`}</option>
                      <option value="below">{t`Below`}</option>
                    </select>

                    <input
                      className="NM-input"
                      type="number"
                      placeholder={t`Target price $`}
                      value={newAlertPrice}
                      onChange={(e) => setNewAlertPrice(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addPriceAlert()}
                    />
                  </div>
                  <div className="NM-add-alert-actions">
                    <button className="NM-btn-primary NM-btn-sm" onClick={addPriceAlert}>
                      <Trans>Save Alert</Trans>
                    </button>
                    <button
                      className="NM-btn-ghost NM-btn-sm"
                      onClick={() => { setAddingAlert(false); setNewAlertPrice(""); }}
                    >
                      <Trans>Cancel</Trans>
                    </button>
                  </div>
                </div>
              )}

              {prefs.priceAlerts.length === 0 && !addingAlert && (
                <div className="NM-empty">
                  <Trans>No price alerts set. Click + Add to create one.</Trans>
                </div>
              )}

              {prefs.priceAlerts.map((alert) => (
                <div key={alert.id} className={cx("NM-alert-row", { "NM-alert-row-off": !alert.enabled })}>
                  <div className="NM-alert-info">
                    <span className="NM-alert-symbol">{alert.symbol}</span>
                    <span className="NM-alert-desc">
                      {alert.direction === "above" ? "↑ above" : "↓ below"}{" "}
                      ${alert.targetPrice.toLocaleString()}
                    </span>
                  </div>
                  <div className="NM-alert-actions">
                    <Toggle
                      checked={alert.enabled}
                      onChange={() => toggleAlert(alert.id)}
                    />
                    <button
                      className="NM-alert-delete"
                      onClick={() => deleteAlert(alert.id)}
                      title={t`Delete alert`}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </section>
          </>
        )}

        {/* ── Email notifications ──────────────────────────── */}
        <div className="NM-divider" />
        <section className="NM-section">
          <div className="NM-section-title NM-mb8"><Trans>Email Notifications</Trans></div>
          {!account ? (
            <div className="NM-warn"><Trans>Connect your wallet to save email alerts.</Trans></div>
          ) : (
            <div className="NM-email-row">
              <input
                className={cx("NM-input NM-email-input", { "NM-input-saved": emailSaved })}
                type="email"
                placeholder={t`your@email.com`}
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
              />
              {emailSaving && <span className="NM-email-status"><Trans>Saving…</Trans></span>}
              {emailSaved && <span className="NM-email-status NM-email-ok">✓ <Trans>Saved</Trans></span>}
            </div>
          )}
          {account && email.includes("@") && (
            <p className="NM-email-hint">
              <Trans>You'll receive alerts for liquidation risk and trade confirmations at this address.</Trans>
            </p>
          )}
        </section>

        <div className="NM-footer">
          <Trans>Alerts fire in your browser while the tab is open.</Trans>
        </div>
      </div>
    </Modal>
  );
}

// ── Sub-components ────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label?: string;
}) {
  return (
    <button
      className={cx("NM-toggle", { "NM-toggle-on": checked })}
      onClick={onChange}
      role="switch"
      aria-checked={checked}
    >
      <span className="NM-toggle-thumb" />
      {label && <span className="NM-toggle-label">{label}</span>}
    </button>
  );
}

function AlertRow({
  icon,
  title,
  body,
  checked,
  onChange,
}: {
  icon: string;
  title: string;
  body: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="NM-alert-type-row">
      <span className="NM-alert-icon">{icon}</span>
      <div className="NM-alert-text">
        <div className="NM-alert-title">{title}</div>
        <div className="NM-alert-body">{body}</div>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}
